import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useTheme } from '@emotion/react'
import { linkUnderline } from 'chaoskit/src/assets/styles/utility/link'
import { Row, RowColumn } from 'chaoskit/src/components'
import { StylesAvatarVariables } from 'chaoskit/src/components/Avatar'
import { text } from 'chaoskit/src/assets/styles/utility'

import Image from './Image'

import ButtonLink from '~components/ButtonLink'
import { ReactComponent as QuoteSvg } from '~media/global/quote.svg'

const Author = ({ author }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        color: `${theme.color.primary.base} !important`,
        fontWeight: '500',
        ...theme.text['2xlarge'],
        lineHeight: theme.lineHeight.base,
      }}
      className="G__Quote__Author"
    >
      {author}
    </div>
  )
}

Author.propTypes = {
  author: PropTypes.string,
}

const Avatar = ({ author, avatar }) => {
  const theme = useTheme()

  return (
    <Image
      alt={author}
      css={{
        position: 'absolute',
        right: theme.space.small,
        bottom: theme.space.small,
        width: StylesAvatarVariables(theme).size.small,
        height: StylesAvatarVariables(theme).size.small,
        objectFit: 'cover',
        boxShadow: theme.boxShadow.base,
        borderRadius: theme.borderRadius.rounded,
        zIndex: 1,

        [theme.mq.medium]: {
          right: theme.space.base,
          bottom: theme.space.base,
          width: StylesAvatarVariables(theme).size.base,
          height: StylesAvatarVariables(theme).size.base,
        },
      }}
      src={avatar}
    />
  )
}

Avatar.propTypes = {
  author: PropTypes.string,
  avatar: PropTypes.string,
}

const QuoteTitle = ({ title }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        ...text.expanded(theme),
        ...theme.text.large,
        marginBottom: theme.space.base,
        marginTop: theme.space.xsmall,
        fontWeight: theme.fontWeight.bold,
      }}
      className="G__Quote__Title"
    >
      {title}
    </div>
  )
}

QuoteTitle.propTypes = {
  title: PropTypes.string,
}

export const QuoteLogo = ({ logo, company }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'inline-block',
        width: '50%',
      }}
    >
      <Image src={logo} alt={company} style={{ height: theme.height.small }} />
    </div>
  )
}

QuoteLogo.propTypes = {
  logo: PropTypes.string,
  company: PropTypes.string,
}

const QuoteCTA = () => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'inline-block',
        width: '100%',
        textAlign: 'left',
        marginTop: theme.space.base,

        [theme.mq.medium]: {
          width: '50%',
          textAlign: 'right',
          marginTop: 0,
        },
      }}
    >
      <ButtonLink noContrast url="/customers/" type="primary">
        Read Case Studies
      </ButtonLink>
    </div>
  )
}

QuoteCTA.propTypes = {}

const OldQuoteLayout = ({ author, avatar, title, company, logo, hideCta }) => (
  <div className="G__Quote__Author__Container">
    {author && <Author author={author} />}
    {avatar && <Avatar author={author} avatar={avatar} />}
    {title && <QuoteTitle title={title} />}
    {logo && <QuoteLogo company={company} logo={logo} />}
    {!hideCta && <QuoteCTA />}
  </div>
)

OldQuoteLayout.propTypes = {
  author: PropTypes.string,
  avatar: PropTypes.string,
  title: PropTypes.string,
  company: PropTypes.string,
  logo: PropTypes.string,
  hideCta: PropTypes.bool,
}

const NewQuoteLayout = ({ author, title, company, logo, hideCta }) => {
  const theme = useTheme()

  return (
    <div className="G__Quote__Author__Container">
      <Row gutter={{ base: 'base', medium: 'base' }}>
        <RowColumn
          size={{ medium: 6 }}
          css={{
            [theme.mq.medium]: {
              marginTop: theme.space.xsmall,
              textAlign: 'right',
            },
          }}
        >
          {logo && <QuoteLogo company={company} logo={logo} />}
        </RowColumn>
        <RowColumn
          size={{ medium: 6 }}
          css={{
            [theme.mq.medium]: {
              textAlign: 'left',
            },
          }}
        >
          {author && <Author author={author} />}
          {title && <QuoteTitle title={title} />}
        </RowColumn>
      </Row>
      <div>{!hideCta && <QuoteCTA />}</div>
    </div>
  )
}

NewQuoteLayout.propTypes = {
  author: PropTypes.string,
  title: PropTypes.string,
  company: PropTypes.string,
  logo: PropTypes.string,
  hideCta: PropTypes.bool,
}

const Quote = ({
  className,
  children,
  contrastQuotes,
  avatar,
  author,
  title,
  company,
  logo,
  size = 'base',
  isContainer,
  hideCta = true,
  newLayout = false,
  ...props
}) => {
  const theme = useTheme()

  return (
    <div
      css={[
        {
          position: 'relative',

          '* + &': {
            marginTop: theme.space.large,
          },

          '&:not(:last-child)': {
            marginBottom: theme.space.large,
          },
        },

        isContainer && {
          paddingLeft: theme.space.medium,
          paddingRight: theme.space.medium,
          paddingTop: theme.space.base,
          paddingBottom: theme.space.base,

          [theme.mq.medium]: {
            paddingLeft: theme.space.large,
            paddingRight: theme.space.large,
          },
        },
      ]}
      className={clsx(`G__Quote ${theme.settings.classes.trim}`, className)}
      {...props}
    >
      <blockquote
        className={theme.settings.classes.trim}
        css={[
          size === 'base' ? theme.text.large__fluid : theme.text.xlarge__fluid,
          {
            // Resets/changes from normal blockquote
            padding: 0,
            border: 0,
            position: 'relative',
            zIndex: 1,
            fontFamily: theme.fontFamily.heading,
            lineHeight: theme.lineHeight.medium,
            fontWeight: theme.fontWeight.bold,

            cite: [
              size === 'base'
                ? theme.text.large__fluid
                : theme.text.xlarge__fluid,
              {
                display: 'block',
                fontStyle: 'normal',
                marginTop:
                  size === 'base' ? theme.fontSize.base : theme.fontSize.large,
                marginBottom: theme.space.small,
                color: theme.brand.teal,

                a: [
                  linkUnderline,
                  {
                    color: 'currentColor',
                  },
                ],

                span: {
                  display: 'block',
                  ...theme.text.small,
                  color: theme.fontColor.muted,
                  ...text.expanded(theme),

                  a: {
                    color: 'currentColor',
                  },
                },
              },
            ],
          },
        ]}
      >
        <div css={{ position: 'relative' }}>
          <QuoteSvg
            css={{
              top: 0,
              left: 0,
              zIndex: -1,
              position: 'absolute',
              pointerEvents: 'none',
              width: theme.fontSize.xlarge,
              height: theme.fontSize.xlarge,
              transform: 'translate(-65%, -40%)',

              [theme.mq.medium]: {
                width: theme.fontSize.large * 2,
                height: theme.fontSize.large * 2,
                transform: size === 'base' ? 'translate(-65%, -50%)' : null,
              },
            }}
          />
          {children}
        </div>
      </blockquote>

      {!newLayout && (
        <OldQuoteLayout
          author={author}
          avatar={avatar}
          title={title}
          company={company}
          logo={logo}
          hideCta={hideCta}
        />
      )}
      {newLayout && (
        <NewQuoteLayout
          author={author}
          title={title}
          company={company}
          logo={logo}
          hideCta={hideCta}
        />
      )}
    </div>
  )
}

Quote.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  contrastQuotes: PropTypes.bool,
  author: PropTypes.any,
  avatar: PropTypes.string,
  title: PropTypes.string,
  logo: PropTypes.string,
  company: PropTypes.string,
  size: PropTypes.oneOf(['base', 'large']),
  isContainer: PropTypes.bool,
  hideCta: PropTypes.bool,
  newLayout: PropTypes.bool,
}

export default Quote
